import { QuestionnaireRequest, QuestionnaireType } from 'src/API';
import { isWithinTimeFrameInDays } from 'src/app/core/api/date.util';

/**
 * Describes the assessment a questionnaire would be linked to. Passed explicitly so the table logic
 * has no hidden dependency on component state or `location.pathname` and can be unit tested directly.
 */
export interface AssessmentLinkContext {
  /** The assessment id, or 'new' when the assessment has not been saved yet. */
  assessmentId: string;
  /** The assessment's `createdAt`, or null when `assessmentId === 'new'` (today is used instead). */
  assessmentCreatedAt: string | null;
}

export interface QuestionnaireRequestsByType {
  [key: string]: QuestionnaireRequest[];
}

/**
 * Pure logic for the questionnaire linking table: groups a patient's questionnaire requests by type
 * and filters each bucket down to the questionnaires that are eligible to be linked to a given
 * assessment (within the allowed time window, and only subsequent questionnaires once one is linked).
 *
 * Dependency-free and instantiated with `new` so it can be unit tested without Angular TestBed,
 * mirroring `QuestionnaireSourceClassifier` under encounters/encounter-linking.
 */
export class LinkQuestionnaireTableBuilder {
  buildTable(
    questionnaireRequests: QuestionnaireRequest[],
    context: AssessmentLinkContext
  ): { questionnaireRequestsByType: QuestionnaireRequestsByType; notEmpty: boolean } {
    const questionnaireRequestsByType = this.groupByType(
      questionnaireRequests,
      this.createEmptyBuckets()
    );
    const notEmpty = this.filterByType(questionnaireRequestsByType, context);
    return { questionnaireRequestsByType, notEmpty };
  }

  createEmptyBuckets(): QuestionnaireRequestsByType {
    return {
      [QuestionnaireType.FITZPATRICK]: [],
      [QuestionnaireType.CDERFS]: [],
      [QuestionnaireType.DERFS]: [],
      [QuestionnaireType.DEQ]: [],
      [QuestionnaireType.SPEED]: [],
      [QuestionnaireType.SPEEDII]: [],
      [QuestionnaireType.OSDI]: [],
      [QuestionnaireType.OSDIAndSPEED]: [],
      [QuestionnaireType.OSDI6]: [],
      [QuestionnaireType.MEDICATION]: [],
      [QuestionnaireType.DEWSIII_Q]: []
    };
  }

  groupByType(
    questionnaireRequests: QuestionnaireRequest[],
    buckets: QuestionnaireRequestsByType
  ): QuestionnaireRequestsByType {
    questionnaireRequests.forEach(questionnaireRequest => {
      if (questionnaireRequest.type === QuestionnaireType.CUSTOM && !questionnaireRequest.config) {
        return;
      }
      const questionnaireType = this.resolveQuestionnaireType(questionnaireRequest);
      if (!buckets[questionnaireType]) {
        buckets[questionnaireType] = [];
      }
      buckets[questionnaireType].push(questionnaireRequest);
    });
    return buckets;
  }

  /** Filters every bucket in place; returns true if any bucket still has questionnaires to link. */
  private filterByType(
    buckets: QuestionnaireRequestsByType,
    context: AssessmentLinkContext
  ): boolean {
    let notEmpty = false;
    Object.keys(buckets).forEach(questionnaireType => {
      const lastCompletedQuestionnaire = this.getLastCompletedLinkedQuestionnaire(
        buckets[questionnaireType],
        context
      );
      if (lastCompletedQuestionnaire) {
        buckets[questionnaireType] = this.filterStartingFromDate(
          lastCompletedQuestionnaire.completedAt,
          buckets[questionnaireType]
        );
      }
      if (
        questionnaireType === QuestionnaireType.CDERFS ||
        questionnaireType === QuestionnaireType.DERFS
      ) {
        buckets[questionnaireType] = this.filterToOneYear(buckets[questionnaireType], context);
      } else {
        buckets[questionnaireType] = this.filterToFourMonths(buckets[questionnaireType], context);
      }

      if (buckets[questionnaireType].length > 0) {
        notEmpty = true;
      }
    });
    return notEmpty;
  }

  getLastCompletedLinkedQuestionnaire(
    bucket: QuestionnaireRequest[],
    context: AssessmentLinkContext
  ): QuestionnaireRequest {
    if (context.assessmentId === 'new') return null;
    const linkedQuestionnaires = bucket.filter(questionnaire => {
      return questionnaire.assessment
        ? questionnaire.assessment.id === context.assessmentId
        : false;
    });
    const mostRecentDate = new Date(
      Math.max.apply(
        null,
        linkedQuestionnaires.map(item => {
          return new Date(item.completedAt);
        })
      )
    );
    const mostRecentObject = linkedQuestionnaires.filter(item => {
      const d = new Date(item.completedAt);
      return d.getTime() === mostRecentDate.getTime();
    })[0];
    return mostRecentObject;
  }

  filterToFourMonths(
    questionnairesToFilter: QuestionnaireRequest[],
    context: AssessmentLinkContext
  ): QuestionnaireRequest[] {
    return this.filterByTimeFrameFromCompletion(questionnairesToFilter, 30 * 4, context);
  }

  filterToOneYear(
    questionnairesToFilter: QuestionnaireRequest[],
    context: AssessmentLinkContext
  ): QuestionnaireRequest[] {
    return this.filterByTimeFrameFromCompletion(questionnairesToFilter, 365, context);
  }

  filterStartingFromDate(
    questionnaireDate: string,
    questionnairesToFilter: QuestionnaireRequest[]
  ): QuestionnaireRequest[] {
    const date = new Date(questionnaireDate);
    const time = date.getTime();
    return questionnairesToFilter.filter(questionnaire => {
      return new Date(questionnaire.completedAt).getTime() >= time;
    });
  }

  filterByTimeFrameFromCompletion(
    questionnairesToFilter: QuestionnaireRequest[],
    timeFrameInDays: number,
    context: AssessmentLinkContext
  ): QuestionnaireRequest[] {
    const assessmentDate = this.resolveAssessmentDate(context);
    assessmentDate.setHours(0, 0, 0, 0);

    return questionnairesToFilter.filter(questionnaire => {
      if (!questionnaire.completedAt) return false;
      const questionnaireDate = new Date(questionnaire.completedAt);

      questionnaireDate.setHours(0, 0, 0, 0);
      return isWithinTimeFrameInDays(assessmentDate, questionnaireDate, timeFrameInDays);
    });
  }

  resolveQuestionnaireType(questionnaire: QuestionnaireRequest): string {
    if (questionnaire.type !== QuestionnaireType.CUSTOM) {
      return questionnaire.type;
    }
    // A CUSTOM questionnaire's type comes from its config's schema abbreviation. Config may be a
    // parsed object or a JSON string, and legacy/malformed configs occur in practice, so fall back
    // to CUSTOM rather than letting a parse or missing-field error break the whole table.
    try {
      const config =
        (questionnaire.config as any) instanceof Object
          ? questionnaire.config
          : JSON.parse(questionnaire.config);
      const abbreviation = config && config.schema && config.schema.abbreviation;
      return abbreviation ? abbreviation : QuestionnaireType.CUSTOM;
    } catch (e) {
      return QuestionnaireType.CUSTOM;
    }
  }

  private resolveAssessmentDate(context: AssessmentLinkContext): Date {
    if (context.assessmentId === 'new' || !context.assessmentCreatedAt) {
      return new Date();
    }
    return new Date(context.assessmentCreatedAt);
  }
}
